import ChatMessageCard from "@/components/Chat/chatMessageCard.component"; // Import the new smaller component
import ChatNoMessages from "@/components/Chat/chatNoMessages.component";
import MessageSendingText from "@/components/Chat/messageSendingText.component"; // Import the message sending text component
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { ChatMessage } from "@/types";
import hljs from "highlight.js/lib/common"; // Import all common languages
import javascript from "highlight.js/lib/languages/javascript"; // Import JavaScript language
import typescript from "highlight.js/lib/languages/typescript"; // Import TypeScript language
import "highlight.js/styles/github.css"; // Import highlight.js theme for code styling
import React, { useEffect, useRef } from "react";

// Register TypeScriptReact as a language by extending TypeScript
hljs.registerLanguage("typescriptreact", typescript);
hljs.registerLanguage("javascriptreact", javascript);

type MessagesScrollAreaProps = {
  messages: ChatMessage[];
  sendingMessage: ChatMessage | null;
  waitingForResponse: boolean;
};

const MessagesScrollArea: React.FC<MessagesScrollAreaProps> = ({
  messages,
  sendingMessage,
  waitingForResponse,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll to bottom when new message is added
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, sendingMessage, waitingForResponse]);

  // No need to highlight code blocks here because markdown-it already outputs highlighted HTML

  return (
    <div className="h-full w-full flex-1 overflow-hidden rounded-md">
      {messages.length === 0 && !sendingMessage ? (
        <ChatNoMessages />
      ) : (
        <ScrollArea
          className="h-full w-full overflow-hidden [&>div:first-of-type]:h-full [&>div:first-of-type>div:first-of-type]:h-full"
          ref={scrollAreaRef}
        >
          <div className="flex h-full flex-col justify-between">
            <div className="w-full space-y-4 overflow-hidden p-4">
              {messages.map((msg) => (
                <ChatMessageCard key={msg.id} message={msg} />
              ))}
              {sendingMessage && (
                <ChatMessageCard
                  key={sendingMessage.id}
                  message={sendingMessage}
                />
              )}
            </div>
            {(waitingForResponse || sendingMessage !== null) && (
              <div className="mt-auto flex w-full p-4">
                <MessageSendingText />
              </div>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

export default MessagesScrollArea;
